import type { MarketingRouterRule } from '~/types/marketingRouterRule';

type Match = MarketingRouterRule['match'];

/** The match fields whose values every admin surface has to read. */
const MATCH_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'url'] as const;

/**
 * Reads one stored match field as the list of values it accepts.
 *
 * `rule_json` is only schema-checked on the admin write path, so a rule
 * written before match fields became lists reads back holding a plain string.
 * The types say `Array<string>`, so every unguarded `.map`/`.join` over a
 * stored field throws on such a rule.
 *
 * A stale string is surfaced as the single value it always meant rather than
 * dropped: this is an authoring surface, and an operator cannot repair a rule
 * whose stored value the form refuses to show them. Matching stays governed by
 * svc-promo, which fails such a field closed until the rule is saved again.
 */
export function toMatchValues(values: unknown): Array<string> {
  const list = Array.isArray(values) ? values : [values];

  return list.filter((value): value is string => typeof value === 'string' && value.trim() !== '');
}

/** Reads every field of a stored match as a list, preserving unset fields. */
export function toMatchLists(match: Match): Match {
  const entries = MATCH_FIELDS.filter((field) => match[field] != null).map((field) => [
    field,
    toMatchValues(match[field]),
  ]);

  return { ...(Object.fromEntries(entries) as Match) };
}

/**
 * Renders a field's values for display. React concatenates an array of
 * children with no separator, so a raw list reads as one run-together word.
 */
export function formatMatchValues(values: unknown): string {
  return toMatchValues(values).join(', ');
}
